import { useEffect, useState } from "react";
import { PRODUCTS, type Product } from "../config";
import { canOpenBrowserWindow, openBrowserWindow } from "../office";

export function ProductChooser({ onChoose }: { onChoose: (product: Product) => void }) {
  return (
    <div className="stack">
      <p className="lede">Sign in to one Jurisio account. Ella in Word talks only to the account you choose.</p>
      {(["firm", "ask"] as const).map((product) => (
        <button key={product} type="button" className="choice" onClick={() => onChoose(product)}>
          <strong>{PRODUCTS[product].label}</strong>
          <span>{product === "firm" ? "For your firm’s matters." : "Your personal Jurisio account."}</span>
          {!PRODUCTS[product].available ? <span>Coming soon.</span> : null}
        </button>
      ))}
    </div>
  );
}

function useSecondsLeft(expiresAt: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return Math.max(0, Math.round(expiresAt - now / 1000));
}

export function PairingScreen({
  product,
  userCode,
  connectUrl,
  expiresAt,
  onCancel,
}: {
  product: Product;
  userCode: string;
  connectUrl: string;
  expiresAt: number;
  onCancel: () => void;
}) {
  const secondsLeft = useSecondsLeft(expiresAt);
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, "0");
  const desktop = canOpenBrowserWindow();
  return (
    <div className="stack">
      <p className="lede">
        Open the {PRODUCTS[product].label} sign-in page in your browser, sign in, and type this code:
      </p>
      <div className="code" aria-label="Pairing code">
        {userCode}
      </div>
      <div className="card stack">
        <span className="url">{connectUrl}</span>
        {desktop ? (
          <button type="button" className="btn btn--primary" onClick={() => openBrowserWindow(connectUrl)}>
            Open sign-in page
          </button>
        ) : (
          <a className="link" href={connectUrl} target="_blank" rel="noopener noreferrer">
            Open sign-in page
          </a>
        )}
      </div>
      <p className="small">
        Waiting for you to confirm… the code expires in {minutes}:{seconds}. Never type a code someone else sent you.
      </p>
      <div className="row">
        <button type="button" className="btn btn--quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function ConfirmScreen({
  product,
  email,
  onConfirm,
  onReject,
}: {
  product: Product;
  email: string;
  onConfirm: () => void;
  onReject: () => void;
}) {
  return (
    <div className="stack">
      <div className="card stack">
        <p className="lede">
          Connected as <strong>{email}</strong> ({PRODUCTS[product].label}).
        </p>
        <p className="small">Ella will not read this document until you confirm this is your account.</p>
      </div>
      <div className="row">
        <button type="button" className="btn btn--primary" onClick={onConfirm}>
          Confirm
        </button>
        <button type="button" className="btn" onClick={onReject}>
          That’s not me — sign out
        </button>
      </div>
    </div>
  );
}
